// src/Components/AuthRequired.tsx

import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import axios from 'axios';
import useAuth from '../hooks/useAuth.ts';

const AuthRequired: React.FC = () => {
  const {
    authenticated,
    setAuthenticated,
    setName,
    setEmail,
    setRoles,
    setOrganizationIds,
  } = useAuth();
  const location = useLocation();
  const [checking, setChecking] = useState(true);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    checkAuthentication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const checkAuthentication = async () => {
    const token = localStorage.getItem('token');

    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      try {
        const { data } = await axios.get(`${backendUrl}/api/auth/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (data.authenticated) {
          setAuthenticated(true);
          setName(localStorage.getItem('name'));
          setEmail(localStorage.getItem('email'));
          setRoles(JSON.parse(localStorage.getItem('roles') || 'null'));
          setOrganizationIds(JSON.parse(localStorage.getItem('organizationIds') || 'null'));
          setChecking(false);
          return;
        }
        clearSession();
      } catch {
        clearSession();
      }
    }

    // No valid token. Sign-in completes via the Microsoft → EasyAuth →
    // /api/auth/finish-login → /auth/callback#jwt=... handoff (see AuthCallback),
    // which stores the JWT before routing into a protected page. So reaching
    // here without a token means the user is genuinely signed out — no
    // cross-site cookie read against /api/auth/microsoft/callback.
    setAuthenticated(false);
    setChecking(false);
  };

  const clearSession = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('name');
    localStorage.removeItem('email');
    localStorage.removeItem('roles');
    localStorage.removeItem('organizationIds');
    delete axios.defaults.headers.common['Authorization'];
  };

  if (checking) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '20px',
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '3px solid #f3f3f3',
          borderTop: '3px solid #0078d4',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }} />
        <p>Verifying authentication...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!authenticated) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  return <Outlet />;
};

export default AuthRequired;
